import React, { useState, useEffect, useRef } from 'react'
import { T, SectionLabel } from '../ui'
import { getPushEndpoint, getNFLNotifyPrefs, setNFLNotifyPrefs, searchNFLPlayers } from '../api'

// Custom notification settings — the panel that appears once a device is subscribed. Four
// sections (alert types, watchlist, thresholds, scheduled digest) that read/write the
// device's prefs via /nfl/push/prefs. Auto-saves each change. Compliance-safe: every alert
// is graded results or model output — no picks, no stake language.
const TEAMS = ['ARI', 'ATL', 'BAL', 'BUF', 'CAR', 'CHI', 'CIN', 'CLE', 'DAL', 'DEN', 'DET', 'GB',
  'HOU', 'IND', 'JAX', 'KC', 'LV', 'LAC', 'LA', 'MIA', 'MIN', 'NE', 'NO', 'NYG', 'NYJ', 'PHI',
  'PIT', 'SF', 'SEA', 'TB', 'TEN', 'WAS']
const DAYS = [['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun'], ['mon', 'Mon']]
const TYPES = [
  ['recap', 'Results recap', 'How the graded plays did each week'],
  ['pregame', 'Board is live', "When the week's plays post"],
  ['steam', 'Line moves', 'Market steaming toward our side'],
  ['signals', 'Shootout signals', 'High-total / smash-spot games'],
]
const PROB_OPTS = [[0, 'Off'], [0.6, '60%+'], [0.65, '65%+'], [0.7, '70%+'], [0.75, '75%+'], [0.8, '80%+']]
const MOVE_OPTS = [[0, 'Off'], [1, '1+ pt'], [2, '2+ pts'], [3, '3+ pts'], [4, '4+ pts'], [5, '5+ pts']]
const HOURS = Array.from({ length: 24 }, (_, h) => [h, `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`])

function Switch({ on, onClick }) {
  return (
    <button onClick={onClick} style={{ flexShrink: 0, width: 44, height: 26, borderRadius: 13, cursor: 'pointer',
      border: 'none', background: on ? T.green : T.card2, position: 'relative', transition: 'background .15s' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: '50%',
        background: on ? '#04140D' : T.muted, transition: 'left .15s' }} />
    </button>
  )
}

function Chip({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 800, padding: '6px 10px',
      borderRadius: 8, border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
      color: on ? T.green : T.muted }}>{children}</button>
  )
}

const Row = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0' }}>{children}</div>
)

export default function NotifySettings() {
  const [endpoint, setEndpoint] = useState(null)
  const [prefs, setPrefs] = useState(null)
  const [saved, setSaved] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const savedTimer = useRef(null)

  useEffect(() => {
    let a = true
    getPushEndpoint().then(ep => {
      if (!a || !ep) { setEndpoint(false); return }
      setEndpoint(ep)
      getNFLNotifyPrefs(ep).then(r => { if (a) setPrefs(r.prefs || {}) })
    })
    return () => { a = false }
  }, [])

  // Debounced player search.
  useEffect(() => {
    if (!q.trim()) { setResults([]); return }
    const t = setTimeout(() => { searchNFLPlayers(q).then(r => setResults((r.players || []).slice(0, 6))) }, 250)
    return () => clearTimeout(t)
  }, [q])

  if (endpoint === false) return null            // device not subscribed — nothing to configure
  if (!prefs) return <div style={{ fontSize: 12, color: T.muted, padding: '8px 0' }}>Loading your alert settings…</div>

  const save = next => {
    setPrefs(next)
    setNFLNotifyPrefs(endpoint, next)
    setSaved(true)
    clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setSaved(false), 1400)
  }
  const upd = (section, patch) => save({ ...prefs, [section]: { ...(prefs[section] || {}), ...patch } })
  const types = prefs.types || {}
  const watch = prefs.watchlist || {}
  const th = prefs.thresholds || {}
  const dig = prefs.digest || {}
  const names = watch.player_names || []
  const teams = watch.teams || []

  const addPlayer = p => {
    const nm = p.full_name
    if (!nm || names.map(n => n.toLowerCase()).includes(nm.toLowerCase())) return
    upd('watchlist', { player_names: [...names, nm] })
    setQ(''); setResults([])
  }
  const removePlayer = nm => upd('watchlist', { player_names: names.filter(n => n !== nm) })
  const toggleTeam = t => upd('watchlist', { teams: teams.includes(t) ? teams.filter(x => x !== t) : [...teams, t] })
  const toggleDay = d => {
    const days = dig.days || []
    upd('digest', { days: days.includes(d) ? days.filter(x => x !== d) : [...days, d] })
  }

  const box = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '4px 14px', marginBottom: 12 }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <SectionLabel>Notification settings</SectionLabel>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.green, opacity: saved ? 1 : 0, transition: 'opacity .2s' }}>Saved ✓</span>
      </div>

      {/* Alert types */}
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: T.faint, textTransform: 'uppercase', marginBottom: 4 }}>Alert types</div>
      <div style={box}>
        {TYPES.map(([k, label, desc], i) => (
          <div key={k} style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
            <Row>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{desc}</div>
              </div>
              <Switch on={types[k] !== false} onClick={() => upd('types', { [k]: types[k] === false })} />
            </Row>
          </div>
        ))}
      </div>

      {/* Watchlist */}
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: T.faint, textTransform: 'uppercase', marginBottom: 4 }}>Watchlist — ping me about my guys</div>
      <div style={{ ...box, padding: '12px 14px' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Add a player…" style={{
          width: '100%', boxSizing: 'border-box', background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8,
          padding: '9px 11px', color: T.text, fontSize: 13, fontFamily: 'inherit' }} />
        {results.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {results.map(p => (
              <button key={p.gsis_id || p.full_name} onClick={() => addPlayer(p)} style={{ textAlign: 'left', cursor: 'pointer',
                background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 11px', color: T.text, fontSize: 13 }}>
                <b>{p.full_name}</b> <span style={{ color: T.muted, fontSize: 11 }}>{[p.position, p.team].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </div>
        )}
        {names.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {names.map(nm => (
              <span key={nm} onClick={() => removePlayer(nm)} style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
                padding: '5px 9px', borderRadius: 8, background: T.greenDim, border: `1px solid ${T.green}`, color: T.green }}>
                {nm} ✕
              </span>
            ))}
          </div>
        )}
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', color: T.faint, textTransform: 'uppercase', margin: '14px 0 7px' }}>Teams</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TEAMS.map(t => <Chip key={t} on={teams.includes(t)} onClick={() => toggleTeam(t)}>{t}</Chip>)}
        </div>
      </div>

      {/* Thresholds */}
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: T.faint, textTransform: 'uppercase', marginBottom: 4 }}>Thresholds — ping me when…</div>
      <div style={box}>
        <Row>
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>A featured play hits</div>
          <select value={th.min_prob || 0} onChange={e => upd('thresholds', { min_prob: parseFloat(e.target.value) })}
            style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 9px', color: T.text, fontSize: 13, fontFamily: 'inherit' }}>
            {PROB_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <div style={{ borderTop: `1px solid ${T.border}` }}>
          <Row>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>Only ELITE plays</div>
              <div style={{ fontSize: 11, color: T.muted }}>Highest-conviction tier only</div>
            </div>
            <Switch on={!!th.elite_only} onClick={() => upd('thresholds', { elite_only: !th.elite_only })} />
          </Row>
        </div>
        <div style={{ borderTop: `1px solid ${T.border}` }}>
          <Row>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>Line moves our way</div>
            <select value={th.min_line_move || 0} onChange={e => upd('thresholds', { min_line_move: parseFloat(e.target.value) })}
              style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 9px', color: T.text, fontSize: 13, fontFamily: 'inherit' }}>
              {MOVE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Row>
        </div>
      </div>

      {/* Scheduled digest */}
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: T.faint, textTransform: 'uppercase', marginBottom: 4 }}>Scheduled digest</div>
      <div style={box}>
        <Row>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>Daily top plays</div>
            <div style={{ fontSize: 11, color: T.muted }}>A digest of the top featured plays</div>
          </div>
          <Switch on={!!dig.enabled} onClick={() => upd('digest', { enabled: !dig.enabled })} />
        </Row>
        {dig.enabled && (
          <div style={{ borderTop: `1px solid ${T.border}`, paddingBottom: 10 }}>
            <Row>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Time (ET)</div>
              <select value={dig.hour ?? 11} onChange={e => upd('digest', { hour: parseInt(e.target.value) })}
                style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 9px', color: T.text, fontSize: 13, fontFamily: 'inherit' }}>
                {HOURS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Row>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 2 }}>
              {DAYS.map(([d, l]) => <Chip key={d} on={(dig.days || []).includes(d)} onClick={() => toggleDay(d)}>{l}</Chip>)}
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize: 10, color: T.faint, lineHeight: 1.5, padding: '0 2px 4px' }}>
        Alerts are graded results and model output — research, not betting advice · 21+. Settings apply to this device.
      </div>
    </div>
  )
}
